import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";

const DISCIPLINES = [
  "Python", "DevOps", "Robotics", "QA Testing", "C++ Systems",
  "Mathematics", "English", "Electronics", "Kubernetes", "Scratch for Kids",
];

const STATS = [
  { v: "47+", l: "courses" },
  { v: "10",  l: "disciplines" },
  { v: "500+",l: "students" },
];

// Keyframe animations injected once
const ANIM_CSS = `
@keyframes floatPill {
  0%,100% { transform: translateY(0px) rotate(var(--r,0deg)); }
  50%      { transform: translateY(-10px) rotate(var(--r,0deg)); }
}
@keyframes fadeSlideIn {
  from { opacity:0; transform:translateY(16px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes pulseDot {
  0%,100% { opacity:1; transform:scale(1); }
  50%     { opacity:.5; transform:scale(.8); }
}
`;

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password);
      const from = location.state?.from?.pathname;
      if (from) navigate(from, { replace: true });
      else if (u.role === "admin") navigate("/admin");
      else if (u.role === "teacher") navigate("/teacher");
      else navigate("/dashboard");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2 border-b border-eduhu-border" data-testid="login-page">
      <style>{ANIM_CSS}</style>

      {/* ── Left decorative panel ─────────────────────────────────────────── */}
      <div className="hidden lg:flex bg-eduhu-dark text-eduhu-bg grain p-12 flex-col justify-between overflow-hidden relative">

        {/* Floating discipline pills */}
        <div className="absolute inset-0 pointer-events-none select-none" aria-hidden>
          {DISCIPLINES.map((d, i) => (
            <span
              key={d}
              style={{
                position: "absolute",
                top: `${10 + (i * 8.5) % 78}%`,
                left: `${5 + (i * 17) % 72}%`,
                animationName: "floatPill",
                animationDuration: `${3.5 + (i % 4) * 0.8}s`,
                animationTimingFunction: "ease-in-out",
                animationIterationCount: "infinite",
                animationDelay: `${i * 0.4}s`,
                "--r": `${-3 + (i % 5)}deg`,
                opacity: 0.12 + (i % 3) * 0.06,
              }}
              className="text-xs font-mono tracking-widest uppercase text-eduhu-gold border border-eduhu-gold/20 px-2.5 py-1 whitespace-nowrap"
            >
              {d}
            </span>
          ))}
        </div>

        {/* Heading */}
        <div style={{ animation: "fadeSlideIn .7s ease both" }}>
          <div className="font-serif text-6xl text-eduhu-gold leading-none">
            Welcome<br />back.
          </div>
          <div className="mt-6 flex gap-6">
            {STATS.map((s, i) => (
              <div key={s.l} style={{ animation: `fadeSlideIn .7s ease ${.2 + i * .15}s both` }}>
                <div className="font-serif text-3xl text-eduhu-gold">{s.v}</div>
                <div className="text-xs text-eduhu-bg/50 uppercase tracking-widest">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Live indicator + quote */}
        <div style={{ animation: "fadeSlideIn .7s ease .6s both" }}>
          <div className="flex items-center gap-2 mb-4">
            <span style={{ animation: "pulseDot 2s ease-in-out infinite" }}
              className="w-2 h-2 rounded-full bg-eduhu-success inline-block" />
            <span className="text-xs text-eduhu-bg/50 uppercase tracking-widest">Live classes available now</span>
          </div>
          <div className="text-sm text-eduhu-bg/60 max-w-xs leading-relaxed">
            "We open the doors of practical mastery — to the curious, the disciplined, the brave."
          </div>
        </div>
      </div>

      {/* ── Right form panel ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="flex justify-center mb-8">
            <Link to="/" aria-label="EDUHUB home">
              <img src="/logo.png" alt="EDUHUB logo" className="w-16 h-16 object-contain" />
            </Link>
          </div>

          <div className="overline text-eduhu-claret mb-3">{t("nav.signin")}</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-eduhu-dark tracking-tight">
            Sign in to EDUHUB
          </h1>

          <label className="block mt-10">
            <div className="overline text-eduhu-inkSoft mb-2">{t("auth.email")}</div>
            <input
              data-testid="email-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-eduhu-bg2 border border-eduhu-border px-4 py-3 focus:outline-none focus:border-eduhu-dark"
            />
          </label>

          <label className="block mt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="overline text-eduhu-inkSoft">{t("auth.password")}</div>
              <Link to="/forgot-password" className="text-xs text-eduhu-claret nav-link">
                Forgot password?
              </Link>
            </div>
            <input
              data-testid="password-input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-eduhu-bg2 border border-eduhu-border px-4 py-3 focus:outline-none focus:border-eduhu-dark"
            />
          </label>

          {err && (
            <div className="mt-4 text-sm text-eduhu-error" data-testid="login-error">{err}</div>
          )}

          <button
            data-testid="login-submit"
            disabled={busy}
            className="eduhu-btn-primary w-full justify-center mt-8"
          >
            {busy ? "…" : t("auth.signin")}
          </button>

          <div className="mt-6 text-sm text-eduhu-inkSoft text-center">
            {t("auth.noAccount")}{" "}
            <Link to="/register" data-testid="to-register" className="text-eduhu-claret nav-link">
              {t("auth.signup")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
